import { collection, getDocs, query } from "firebase/firestore";
import { auth, db } from "../firebase/client";
import type { Patient } from "../contexts/types";

export interface RegistryRankItem {
  label: string;
  count: number;
}

export interface ClinicalRegistryStats {
  totalCases: number;
  unionRate: number | null;
  complicationRate: number | null;
  topTreatmentStrategies: RegistryRankItem[];
  topBones: RegistryRankItem[];
}

const normalize = (value?: string) => (value || "").trim().toLowerCase();

const rankValues = (values: string[], maxItems = 5): RegistryRankItem[] => {
  const counts = values.reduce((acc, rawValue) => {
    const label = rawValue.trim();
    if (!label) return acc;

    const key = normalize(label);
    const current = acc[key];
    if (current) {
      current.count += 1;
      return acc;
    }

    acc[key] = { label, count: 1 };
    return acc;
  }, {} as Record<string, RegistryRankItem>);

  return Object.values(counts)
    .sort((a, b) => b.count - a.count)
    .slice(0, maxItems);
};

const getTextValue = (value: unknown) =>
  typeof value === "string" ? value.trim() : "";

const getTreatmentStrategy = (patient: Patient) =>
  getTextValue(patient.clinicalRegistry?.treatment.strategy) ||
  getTextValue(patient.caseFingerprint?.treatmentStrategy) ||
  getTextValue((patient as any).treatment) ||
  getTextValue((patient as any).treatmentStrategy) ||
  getTextValue(patient.surgicalPlan);

const getBoneValue = (patient: Patient) =>
  getTextValue(patient.clinicalRegistry?.diagnosis.bone) ||
  getTextValue(patient.caseFingerprint?.bone) ||
  getTextValue((patient as any).anatomicalRegion) ||
  getTextValue(patient.anatomy);

export function buildClinicalRegistryStats(
  patients: Patient[]
): ClinicalRegistryStats {
  const totalCases = patients.length;
  const registryPatients = patients.filter((patient) => !!patient.clinicalRegistry);
  const totalRegistryCases = registryPatients.length;
  const unionCases = registryPatients.filter(
    (patient) => normalize(patient.clinicalRegistry?.outcome.healingStatus) === "union"
  ).length;
  const complicationCases = registryPatients.filter((patient) => {
    const value = normalize(patient.clinicalRegistry?.outcome.complications);
    return !!value && value !== "none";
  }).length;

  return {
    totalCases,
    unionRate:
      totalRegistryCases > 0
        ? Math.round((unionCases / totalRegistryCases) * 100)
        : null,
    complicationRate:
      totalRegistryCases > 0
        ? Math.round((complicationCases / totalRegistryCases) * 100)
        : null,
    topTreatmentStrategies: rankValues(
      patients.map((patient) => getTreatmentStrategy(patient))
    ),
    topBones: rankValues(patients.map((patient) => getBoneValue(patient))),
  };
}

async function loadRegistryPatients(userId?: string): Promise<Patient[]> {
  const resolvedUserId = userId || auth.currentUser?.uid;
  if (!resolvedUserId) {
    return [];
  }

  const userPatientsQuery = query(collection(db, "users", resolvedUserId, "patients"));
  const snap = await getDocs(userPatientsQuery);

  return snap.docs.map((docSnap) => ({
    id: docSnap.id,
    ...(docSnap.data() as any),
  })) as Patient[];
}

export async function getClinicalRegistryStats(userId?: string): Promise<ClinicalRegistryStats> {
  const patients = await loadRegistryPatients(userId);
  return buildClinicalRegistryStats(patients);
}
